import React, { FC, useMemo } from 'react';
import { PartOfApprovedCrpModalProps } from './PartOfApprovedCrpModalProps';
import Modal, { Prompt } from '@ingka/modal';
import Text from '@ingka/text';
import { IconButton, Stack } from '@mui/material';
import SSRIcon from '@ingka/ssr-icon';
import cross from '@ingka/ssr-icon/paths/cross';
import useTranslation from '../../hooks/useTranslation';
import PartOfCrpComboBox from '../PartOfCrpComboBox';
import { Box } from '@mui/system';
import Button from '@ingka/button';
import { getFyNumber } from '../../utils/calculation';

const getAllFys = (project: any) => {
  return project?.partOfCrp?.map((c) => c.fiscalYear) || [];
};

const getApprovedFys = (project: any) => {
  return (
    project?.partOfCrp?.filter((e) => e.approved).map((c) => c.fiscalYear) || []
  );
};

const PartOfApprovedCrpModal: FC<PartOfApprovedCrpModalProps> = ({
  visible,
  approveHandler,
  closeHandler,
  projects,
  selectedProjects,
  onCommonCrpFyChangeHandler,
}) => {
  const t = useTranslation();
  const [partOfCrp, setPartOfCrp] = React.useState<any>([]);

  const [commonNotApprovedCrpFys, commonApprovedCrp] = useMemo(() => {
    const selectedProjectIdSet = new Set(selectedProjects);
    const selectedProjectsArr: any[] =
      projects.filter((e) => selectedProjectIdSet.has(e.id)) || [];

    const commonNotApprovedFysArr = selectedProjectsArr.reduce(
      (acc, project) => {
        const projectApprovedFys = getAllFys(project);
        return acc.filter((fy) => projectApprovedFys.includes(fy));
      },
      getAllFys(selectedProjectsArr[0]),
    );
    const commonApprovedArr = selectedProjectsArr.reduce((acc, project) => {
      const projectApprovedFys = getApprovedFys(project);
      return acc.filter((fy) => projectApprovedFys.includes(fy));
    }, getApprovedFys(selectedProjectsArr[0]));

    if (onCommonCrpFyChangeHandler) {
      onCommonCrpFyChangeHandler(
        commonNotApprovedFysArr.length > 0 || commonApprovedArr.length > 0,
      );
    }

    return [commonNotApprovedFysArr, commonApprovedArr];
  }, [selectedProjects, visible, projects]);

  const preselectedCommonApprovedCrp = useMemo(() => {
    const commonApproved = commonApprovedCrp.map((e) => ({ fiscalYear: e }));
    const combinedArr = [...commonApproved, ...partOfCrp].filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.fiscalYear === item.fiscalYear),
    );
    combinedArr.sort(
      (a, b) => getFyNumber(a.fiscalYear) - getFyNumber(b.fiscalYear),
    );
    return combinedArr;
  }, [commonApprovedCrp]);

  const allowedDropDownValues = useMemo(() => {
    const allCommonCrp = [...commonApprovedCrp, ...commonNotApprovedCrpFys];
    allCommonCrp.sort((a, b) => getFyNumber(a) - getFyNumber(b));
    return allCommonCrp;
  }, [commonApprovedCrp, commonNotApprovedCrpFys]);

  const confirm = () => {
    const fyArr = partOfCrp.map((e) => e.fiscalYear);
    approveHandler(fyArr);
    setPartOfCrp([]);
  };

  const close = () => {
    closeHandler();
    setPartOfCrp([]);
  };

  return (
    <Box
      sx={{
        '& .prompt__content-wrapper > div': {
          overflow: 'visible',
        },
        '& div[aria-labelledby="part-of-crp-modal-title"]': {
          overflow: 'visible',
        },
        '& .prompt__content-wrapper': {
          overflow: 'visible',
        },
      }}
    >
      <Modal
        visible={visible}
        handleCloseBtn={close}
        data-testid={'part-of-crp-modal'}
      >
        <Prompt
          header={<></>}
          titleId={'part-of-crp-modal-title'}
          title={
            <Stack mt={2}>
              <Stack
                alignSelf={'end'}
                position={'relative'}
                sx={{ right: '-15px' }}
              >
                <IconButton onClick={close}>
                  <SSRIcon paths={cross} />
                </IconButton>
              </Stack>
              <Stack mb={2}>{t('common.partOfApprovedCRPPlan')}</Stack>
              <Text style={{ fontSize: 16 }}>
                {t('common.partOfApprovedCRPDescription')}
              </Text>
            </Stack>
          }
          footer={<></>}
        >
          <Stack spacing={2} mb={2}>
            <PartOfCrpComboBox
              partOfCrp={preselectedCommonApprovedCrp}
              handleUpdate={setPartOfCrp}
              dropDownValueFilter={allowedDropDownValues}
              label={t('labels.chooseCrpFy')}
            />
          </Stack>
          <Stack flexDirection={'row'} gap={2} mb={2}>
            <Button
              text={t('common.cancel')}
              data-testid={'crp-cancel'}
              type="secondary"
              onClick={close}
              fluid={true}
            />
            <Button
              text={t('common.confirm')}
              data-testid={'crp-confirm'}
              type="primary"
              onClick={confirm}
              fluid={true}
            />
          </Stack>
        </Prompt>
      </Modal>
    </Box>
  );
};

export default PartOfApprovedCrpModal;
